import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/lib/api'

/**
 * KAN-144 (T1 — account deletion and exit rights) / GitHub #814.
 *
 * Self-service account deletion with a 30-day reversible grace window.
 * Server contract: apps/api/src/account-deletion/router.ts. The confirm
 * phrase is enforced server-side too — the input here is friction, not the
 * security boundary.
 */

const CONFIRM_PHRASE = 'DELETE'

interface DeletionRequestWire {
  id: string
  status: 'pending' | 'cancelled' | 'completed'
  requestedAt: string
  effectiveAt: string
  cancelledAt: string | null
  completedAt: string | null
}

interface DeletionStatusResponse {
  graceDays: number
  request: DeletionRequestWire | null
}

export function AccountDeletionSection() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  // Deletion status is PER ACCOUNT: keying the cache on the session subject
  // means an identity change (re-login without a reload) can never serve the
  // previous user's pending-deletion row.
  const sub = user?.sub ?? ''
  const queryKey = ['account-deletion', sub] as const
  const [confirmText, setConfirmText] = useState('')

  const statusQuery = useQuery({
    queryKey,
    queryFn: () => api.get<DeletionStatusResponse>('/me/account-deletion'),
    enabled: sub !== '',
  })

  const requestDeletion = useMutation({
    mutationFn: () =>
      api.post<{ request: DeletionRequestWire; alreadyPending: boolean }>(
        '/me/account-deletion',
        { confirm: CONFIRM_PHRASE },
      ),
    onSuccess: () => {
      setConfirmText('')
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  const cancelDeletion = useMutation({
    mutationFn: () =>
      api.delete<{ request: DeletionRequestWire }>('/me/account-deletion'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  if (statusQuery.isPending) {
    return (
      <p className="text-sm text-muted-foreground">
        Checking your account status…
      </p>
    )
  }

  if (statusQuery.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Your account status could not be loaded. Reload the page to try again.
      </p>
    )
  }

  const graceDays = statusQuery.data.graceDays
  const pending =
    statusQuery.data.request?.status === 'pending'
      ? statusQuery.data.request
      : null

  if (pending) {
    const effectiveDate = new Date(pending.effectiveAt).toLocaleDateString(
      undefined,
      { year: 'numeric', month: 'long', day: 'numeric' },
    )
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium">
          Your account deletion is scheduled for {effectiveDate}.
        </p>
        <p className="max-w-prose text-sm text-muted-foreground">
          You can keep using IntelHub until then. If you change your mind,
          cancel below any time before that date and nothing will be removed.
          After it, your saved items and preferences are deleted permanently
          and your sign-in access is revoked.
        </p>
        {cancelDeletion.isError && (
          <p role="alert" className="text-sm text-destructive">
            The cancellation could not be submitted. Please try again.
          </p>
        )}
        <Button
          variant="outline"
          className="w-fit"
          disabled={cancelDeletion.isPending}
          onClick={() => cancelDeletion.mutate()}
        >
          {cancelDeletion.isPending ? 'Cancelling…' : 'Keep my account'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="max-w-prose space-y-2 text-sm text-muted-foreground">
        <p>
          This will permanently delete your IntelHub account. Your
          preferences, watches, saved searches, notes and tracked vendors are
          removed, your sign-in access is revoked, and we ask our analytics
          processor to erase your usage data. Records we must keep for audit
          integrity are anonymised, not kept under your name — the details are
          in{' '}
          <a
            href="/docs/account-deletion.md"
            target="_blank"
            rel="noopener"
            className="font-medium text-primary underline underline-offset-4"
          >
            how account deletion works
          </a>
          .
        </p>
        <p>
          Deletion takes effect after a {graceDays}-day grace period. Until
          then you can cancel from this page and keep everything. Consider
          downloading your data export first.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="account-deletion-confirm">
          Type {CONFIRM_PHRASE} to confirm
        </Label>
        <Input
          id="account-deletion-confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          className="max-w-48"
        />
      </div>

      {requestDeletion.isError && (
        <p role="alert" className="text-sm text-destructive">
          The deletion request could not be submitted. Please try again.
        </p>
      )}

      <Button
        variant="destructive"
        className="w-fit"
        disabled={confirmText !== CONFIRM_PHRASE || requestDeletion.isPending}
        onClick={() => requestDeletion.mutate()}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        {requestDeletion.isPending ? 'Submitting…' : 'Delete my account'}
      </Button>
    </div>
  )
}
